/**
 * Tracked playtests — INVENTORY-DESKTOP §4 row 5 (lane D4). Prototype lines
 * 1003-1026. Mounted by `DesktopApp` while `s.ptTrackedOpen`.
 *
 * `selectors.3e#ptTrackedVM` + `PT_TRACKED_FOOT` unchanged;
 * `selectors.d4#ptTrackedVMDesktop` wraps it only to swap the `✕`'s mobile
 * 13px tap-target sizing for the prototype's 11px.
 *
 * DESKTOP LAYOUT (§4): 520px, one line per row — dot · 170px name · status ·
 * spacer · source · ✕ (mobile stacked name/status/source in a column).
 *
 * THE ✕ IS TWO CONTROLS IN ONE, and the difference is real in the data layer:
 * an `auto` row is muted for ME only and toasts `MUTED FOR YOU`, so it stays a
 * single tap; anything else is removed CREW-WIDE with cascading rows, so the
 * first tap arms it (`ptRmArm` → the cell reads `SURE?`) and the second removes
 * it. That is the Crew REMOVE pattern, moved here after a stray tap once
 * deleted a seeded playtest for everyone (docs/porting/P4b.md §7,
 * PORTING-NOTES D-04). The prototype has neither the arm nor the split.
 *
 * `style-hover` → CSS (§3.11): `d-hov-acc2` on the ✕, plus `data-tip="UNTRACK"`.
 *
 * Clicking a row's name closes this modal and opens the detail one — the
 * affordance `sheets/PtTrackedSheet.tsx` added; the prototype's rows are inert
 * apart from the ✕.
 */

import { useQd } from '../../store/index.ts';
import { PT_TRACKED_FOOT } from '../../store/selectors.3e.ts';
import { ptTrackedVMDesktop, PT_UNTRACK_TIP } from '../../store/selectors.d4.ts';
import { Modal } from '../components/Modal.tsx';

const MONO = 'var(--gt-font-mono)';

export default function PtTrackedModal(): JSX.Element {
  const s = useQd();
  const v = ptTrackedVMDesktop(s, Date.now());

  return (
    <Modal
      label="Tracked modal"
      width={520}
      onClose={s.closePtTracked}
      header={{ title: '★ TRACKED (' + v.n + ')', titleClr: 'var(--gt-text)' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 0' }}>
        {v.rows.map((t) => (
          <div
            key={t.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 18px',
              borderBottom: '1px solid var(--gt-line-0)',
              opacity: t.dim,
            }}
          >
            <div style={{ width: 8, height: 8, flex: 'none', background: t.dot }} />
            <div
              onClick={() => {
                s.closePtTracked();
                s.openPtDet(t.id);
              }}
              style={{
                fontWeight: 600,
                fontSize: '12.5px',
                color: 'var(--gt-text)',
                width: 170,
                flex: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
              }}
            >
              {t.n}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.04em', color: t.stc }}>{t.st}</div>
            <div style={{ flex: 1 }} />
            <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.04em', color: 'var(--gt-muted-2)' }}>
              {t.src}
            </div>
            <div
              onClick={() => {
                if (t.auto || s.ptRmArm === t.id) void s.ptUntrack(t.id);
                else s.setPtRmArm(t.id);
              }}
              className="d-hov-acc2"
              data-tip={PT_UNTRACK_TIP}
              data-tip-pos="r"
              style={{
                fontFamily: MONO,
                fontSize: t.xFs,
                letterSpacing: '.03em',
                color: t.xClr,
                cursor: 'pointer',
                padding: '2px 5px',
                flex: 'none',
              }}
            >
              {t.xTxt}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 18px 16px', fontFamily: MONO, fontSize: '8.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
        {PT_TRACKED_FOOT}
      </div>
    </Modal>
  );
}
